import { useEffect, useRef, useMemo } from "react";
import Chart from "chart.js/auto";
import Typography from "../../Utility/Typography";
import { mapDashboardWorkItemStatsChart } from "../../utils/dashboardChartMappers";
import DashboardCategorySummary from "./DashboardCategorySummary";

function formatStackValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/**
 * Task statistics: prefers `byColumnAndPriority` — stacked bars for Open / In Progress / Close by priority.
 * Falls back to workflow category bars from `byCategory` / `byStatus`.
 */
const DashboardStatistics = ({ stats }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartModel = useMemo(
    () => mapDashboardWorkItemStatsChart(stats, { palette: "task" }),
    [stats]
  );
  const { labels, datasets, total, categorySummaryParts, stacked } = chartModel;

  const hasData = useMemo(
    () =>
      labels.length > 0 &&
      datasets.length > 0 &&
      datasets.some((ds) => ds.data.some((v) => Number(v) > 0)),
    [labels, datasets]
  );

  const maxY = useMemo(() => {
    if (!datasets.length || !labels.length) return 1;
    if (stacked) {
      let m = 0;
      for (let c = 0; c < labels.length; c++) {
        let col = 0;
        datasets.forEach((ds) => {
          col += Number(ds.data[c]) || 0;
        });
        m = Math.max(m, col);
      }
      return Math.max(m, 1);
    }
    const flat = datasets[0]?.data ?? [];
    return Math.max(...flat.map((v) => Number(v) || 0), 1);
  }, [datasets, labels, stacked]);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!hasData) return;

    const existingChart = Chart.getChart(canvasRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: datasets.map((ds) => ({
          label: ds.label,
          data: ds.data,
          backgroundColor: ds.backgroundColor,
          borderRadius: 3,
          barPercentage: 0.65,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "nearest",
          intersect: !!stacked,
        },
        plugins: {
          legend: {
            display: stacked && datasets.length > 0,
            position: "top",
            align: "center",
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 11 },
            },
          },
          tooltip: {
            mode: "nearest",
            intersect: !!stacked,
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed?.y;
                const label = ctx.dataset?.label ?? "";
                return `${label}: ${formatStackValue(v)}`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: !!stacked,
            grid: { display: false },
            ticks: { maxRotation: 35, autoSkip: false, font: { size: 10 } },
          },
          y: {
            stacked: !!stacked,
            beginAtZero: true,
            suggestedMax: Math.ceil(maxY * 1.1) || 1,
            ticks: { precision: 0 },
            grid: { drawBorder: false },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [labels, datasets, hasData, maxY, stacked]);

  if (!hasData) {
    return (
      <div className="py-10 text-center">
        <Typography variant="text2" className="text-gray-500">
          No task statistics available
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full">
      {categorySummaryParts?.parts?.length ? (
        <DashboardCategorySummary
          parts={categorySummaryParts.parts}
          total={categorySummaryParts.total}
        />
      ) : null}
      {total != null ? (
        <Typography variant="text2" className="text-gray-500 mb-2 block text-xs">
          Reported total: {total}
        </Typography>
      ) : null}
      <div style={{ width: "100%", height: stacked ? 380 : 300 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default DashboardStatistics;
